import { arc, pie } from 'd3-shape';
import { cx } from '../cx';
import { Tooltip } from '../Tooltip/Tooltip';
import { type ChartFocus, ChartFrame, singleColumnTable } from './ChartFrame';
import { type ChartPoint, fillClass, formatValue, NO_DATA, seriesColor } from './chartModel';

/**
 * A RING OF SHARES, WITH THE TOTAL IN THE HOLE (`reports.md` T1 and R4's report 5).
 *
 * A donut answers one question - how a whole divides - so it takes ONE dimension and its
 * slices are the categories. Each slice takes the option's own tone where it has one, so a
 * lifecycle donut is the same six colours as the lifecycle badges in the table beside it.
 *
 * THE HOLE IS NOT DECORATION. It is where the total goes, which is the number a reader
 * needs to turn a share back into a quantity; a ring without it states proportions and
 * refuses to say proportions OF WHAT.
 *
 * NEGATIVE AND NULL VALUES ARE NOT SLICES. A share of a whole cannot be negative, so those
 * points are dropped from the ring and still listed in the table - the honest answer,
 * rather than an arc drawn backwards.
 */
export interface DonutChartProps {
  readonly name: string;
  readonly titleHidden?: boolean | undefined;
  readonly points: readonly ChartPoint[];
  readonly height?: number | undefined;
  readonly loading?: boolean | undefined;
  readonly refetching?: boolean | undefined;
  readonly emptyMessage?: string | undefined;
  readonly categoryHeading?: string | undefined;
  /** The word under the total in the hole: "customers", "deals". */
  readonly totalLabel?: string | undefined;
  readonly format?: ((value: number) => string) | undefined;
  /** A click on a slice. Absent means the ring is a picture and nothing looks pressable. */
  readonly onSelect?: ((point: ChartPoint) => void) | undefined;
}

export function DonutChart({
  name,
  titleHidden,
  points,
  height = 240,
  loading = false,
  refetching = false,
  emptyMessage,
  categoryHeading = 'Category',
  totalLabel,
  format = formatValue,
  onSelect,
}: DonutChartProps) {
  const slices = points.filter((point) => point.value !== null && point.value > 0);
  const empty = slices.length === 0;
  const total = slices.reduce((sum, point) => sum + (point.value ?? 0), 0);

  const legend = points.map((point, index) => ({
    id: point.key,
    label: point.label,
    color: seriesColor(point.tone, index, points.length),
  }));

  const announce = ({ point }: ChartFocus): string => {
    const slice = points[point];
    if (slice === undefined) return '';
    if (slice.value === null) return `${slice.label}: ${NO_DATA}`;
    return `${slice.label}: ${format(slice.value)}, ${share(slice.value, total)} of the total`;
  };

  return (
    <ChartFrame
      name={name}
      {...(titleHidden === undefined ? {} : { titleHidden })}
      height={height}
      loading={loading}
      refetching={refetching}
      {...(emptyMessage === undefined ? {} : { emptyMessage })}
      empty={empty}
      legend={legend}
      table={singleColumnTable(points, categoryHeading, 'Count')}
      rows={1}
      columns={points.length}
      announce={announce}
    >
      {({ width, height: plotHeight, focus }) => (
        <DonutPlot
          width={width}
          height={plotHeight}
          points={points}
          slices={slices}
          total={total}
          focus={focus}
          format={format}
          {...(totalLabel === undefined ? {} : { totalLabel })}
          animate={!refetching}
          {...(onSelect === undefined ? {} : { onSelect })}
        />
      )}
    </ChartFrame>
  );
}

function share(value: number, total: number): string {
  if (total <= 0) return '0%';
  return `${String(Math.round((value / total) * 100))}%`;
}

function DonutPlot({
  width,
  height,
  points,
  slices,
  total,
  focus,
  format,
  totalLabel,
  animate,
  onSelect,
}: {
  readonly width: number;
  readonly height: number;
  readonly points: readonly ChartPoint[];
  readonly slices: readonly ChartPoint[];
  readonly total: number;
  readonly focus: ChartFocus | null;
  readonly format: (value: number) => string;
  readonly totalLabel?: string | undefined;
  readonly animate: boolean;
  readonly onSelect?: ((point: ChartPoint) => void) | undefined;
}) {
  const size = Math.max(1, Math.min(width, height));
  // 4px of headroom, because the FOCUSED slice draws 3px further out than the ring does.
  const outer = size / 2 - 4;
  // A ring rather than a pie: 62% of the radius leaves a hole big enough for the total at
  // 240px, which is the narrowest container the gallery renders.
  const inner = outer * 0.62;
  const centreX = width / 2;
  const centreY = height / 2;

  const layout = pie<ChartPoint>()
    .value((point) => point.value ?? 0)
    .sort(null)
    // From twelve o'clock, clockwise: the reading order everybody expects from a clock face.
    .startAngle(0)
    .endAngle(2 * Math.PI)(slices as ChartPoint[]);

  const shape = arc<(typeof layout)[number]>().innerRadius(inner).outerRadius(outer);
  const focusShape = arc<(typeof layout)[number]>()
    .innerRadius(inner)
    .outerRadius(outer + 3);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      aria-hidden="true"
      className={cx('w-full', animate && 'chart-fade-in')}
    >
      <g transform={`translate(${String(centreX)}, ${String(centreY)})`}>
        {layout.map((slice) => {
          const point = slice.data;
          const index = points.indexOf(point);
          const focused = focus !== null && focus.point === index;
          const path = (focused ? focusShape : shape)(slice);
          if (path === null) return null;
          return (
            <Tooltip
              key={point.key}
              label={`${point.label}: ${format(point.value ?? 0)} (${share(point.value ?? 0, total)})`}
            >
              <path
                d={path}
                {...(onSelect === undefined ? {} : { onClick: () => onSelect(point) })}
                className={cx(
                  fillClass(seriesColor(point.tone, index, points.length)),
                  focused && 'stroke-2 stroke-accent',
                )}
              />
            </Tooltip>
          );
        })}
        <text
          textAnchor="middle"
          y={totalLabel === undefined ? 6 : 0}
          className="fill-ink text-base font-semibold tabular-nums"
        >
          {format(total)}
        </text>
        {totalLabel !== undefined && (
          <text textAnchor="middle" y={18} className="fill-ink-subtle text-2xs">
            {totalLabel}
          </text>
        )}
      </g>
    </svg>
  );
}
